"use client"

import { useState } from "react"
import { Copy, Download, Trash2, Eye, EyeOff } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { useLoggerStore } from "@/lib/logger-store"
import { toast } from "sonner"
import { useTranslations } from "next-intl"

export function LogViewer({ side = "right" }: { side?: "left" | "right" }) {
  const t = useTranslations("logViewer")
  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState<"all" | "info" | "warn" | "error" | "debug">("all")
  const logs = useLoggerStore((state) => state.logs)
  const clearLogs = useLoggerStore((state) => state.clearLogs)
  const exportLogs = useLoggerStore((state) => state.exportLogs)

  const filteredLogs = filter === "all" ? logs : logs.filter((log) => log.level === filter)

  const handleExport = () => {
    const logsJson = exportLogs()
    const blob = new Blob([logsJson], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `logs-${new Date().toISOString().split("T")[0]}.json`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    toast.success(t("logsExportedSuccessfully"))
  }

  const handleCopyJson = () => {
    const logsJson = exportLogs()
    navigator.clipboard.writeText(logsJson)
    toast.success(t("logsCopiedToClipboard"))
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case "error":
        return "bg-red-500/20 text-red-700 dark:text-red-400"
      case "warn":
        return "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400"
      case "debug":
        return "bg-blue-500/20 text-blue-700 dark:text-blue-400"
      default:
        return "bg-gray-500/20 text-gray-700 dark:text-gray-400"
    }
  }

  return (
    <div className={`fixed bottom-4 z-50 ${side === "left" ? "left-4" : "right-4"}`}>
      {isOpen && (
        <div className="mb-3 bg-background border border-border rounded-lg shadow-lg w-96 max-h-96 flex flex-col">
          <div className="p-3 border-b border-border flex items-center justify-between bg-secondary/50">
            <h3 className="text-sm font-semibold">{t("debugLogsLength", { length: filteredLogs.length })}</h3>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="h-6 w-6 p-0">
              <EyeOff className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-2 border-b border-border flex gap-2 flex-wrap">
            {["all", "info", "warn", "error", "debug"].map((level) => (
              <button
                key={level}
                onClick={() => setFilter(level as "all" | "info" | "warn" | "error" | "debug")}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  filter === level ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80"
                }`}
              >
                {level}
              </button>
            ))}
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {filteredLogs.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("noLogs")}</p>
              ) : (
                filteredLogs.map((log, idx) => (
                  <div key={idx} className="text-xs font-mono bg-secondary/40 p-2 rounded">
                    <div className="flex gap-2 items-start">
                      <Badge className={`${getLevelColor(log.level)} text-xs h-5`}>{log.level}</Badge>
                      <span className="text-muted-foreground text-[10px]">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="mt-1 text-foreground">[{log.source}]</div>
                    <div className="text-foreground/80">{log.message}</div>
                    {log.data != null && (
                      <div className="text-foreground/60 break-words">{JSON.stringify(log.data, null, 2)}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          <div className="p-2 border-t border-border flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={handleExport} className="h-7 text-xs gap-1 bg-transparent">
              <Download className="h-3 w-3" />
              {t("export")}
            </Button>
            <Button size="sm" variant="outline" onClick={handleCopyJson} className="h-7 text-xs gap-1 bg-transparent">
              <Copy className="h-3 w-3" />
              {t("copy")}
            </Button>
            <Button size="sm" variant="outline" onClick={clearLogs} className="h-7 text-xs gap-1 bg-transparent">
              <Trash2 className="h-3 w-3" />
              {t("clear")}
            </Button>
          </div>
        </div>
      )}

      <Button
        onClick={() => setIsOpen(!isOpen)}
        size="sm"
        className="rounded-full h-10 w-10 p-0 shadow-lg"
        title={t("toggleDebugLogs")}
      >
        <Eye className="h-4 w-4" />
      </Button>
    </div>
  )
}
